import React, { useState, useContext } from "react";
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Alert,
  TouchableOpacity,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { db } from "../firebase";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";
import sendWelcomeEmail from "../components/SendEmail";
import { Entypo } from "@expo/vector-icons";
import GlobalStyles from "../components/StylenColor";
import { NetworkContext } from "../Context/NetworkContext";
import TermsModal from "../components/TermsModal";
import Checkbox from "expo-checkbox";
import { LinearGradient } from "expo-linear-gradient";

const CompanyRegistrationScreen = ({ navigation }) => {
   const [companyCode, setCompanyCode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [codeExists, setCodeExists] = useState(false);
  const { isConnected } = useContext(NetworkContext);
  const [tcVisible, setTcVisible] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const { width } = useWindowDimensions();
  const isWide = width > 800;

  const validateInputs = () => {
    if (!companyCode || !companyName || !email || !password) {
      showAlert("Error", "All fields are required!");
      return false;
    }

    if (companyName.length < 3) {
      showAlert("Error", "Company name must be at least 3 characters long.");
      return false;
    }

    if (companyCode.length < 4) {
      showAlert("Error", "Company code must be at least 4 characters long.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showAlert("Error", "Please enter a valid email address.");
      return false;
    }

    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/;
    if (!passwordRegex.test(password)) {
      showAlert(
        "Error",
        "Password must be at least 6 characters long, include at least one uppercase letter, one number, and one special character."
      );
      return false;
    }

    return true;
  };

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };
  const checkEmailExists = async (email) => {
    const companiesRef = collection(db, "companies");
    const q = query(companiesRef, where("email", "==", email.toLowerCase()));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  };

  const checkCodeExists = async (code) => {
    const q = query(
      collection(db, "companies"),
      where("companyCode", "==", code.toUpperCase())
    );
    const snap = await getDocs(q);
    setCodeExists(!snap.empty);
  };

  const handleRegister = async () => {
    if (!validateInputs()) return;
    if (emailExists) {
      showAlert("Error", "This email is already in use!");
      return;
    }
    if (codeExists) {
      showAlert("Error", "This company code is already taken!");
      return;
    }
    if (!isConnected) {
      showAlert("No Internet", "You're currently offline.");
      return;
    }
    if (!isAgreed) {
      showAlert("Required", "Please agree to the Terms & Privacy Policy.");
      return;
    }

    setIsLoading(true);

    try {
      await addDoc(collection(db, "companies"), {
        companyCode: companyCode.toUpperCase(),
        name: companyName,
        email: email.toLowerCase(),
        password,
        createdAt: new Date(),
      });

      showAlert("Success", "Company registered successfully!");
      await sendWelcomeEmail(email, companyName, password);
      navigation.navigate("CompanyLoginScreen");
    } catch (error) {
      console.error("Error adding document:", error);
      showAlert("Error", "Failed to register the company!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LinearGradient // Use LinearGradient for the background
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={{ flex: 1 }}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <ScrollView contentContainerStyle={styles.wrapper}>
          <View
            style={[
              styles.container,
              width > 768 && styles.containerWide, // Apply wide styles
            ]}
          >
            <Text
              style={[
                styles.title,
                GlobalStyles.titleTxtOffset,
                isWide && { fontSize: GlobalStyles.fontSize.xl },
              ]}
            >
              Company Registration
            </Text>

            <TextInput
              style={styles.input}
              placeholder="Company Code (used by employees)"
              value={companyCode}
              onChangeText={(text) => {
                setCompanyCode(text);
                checkCodeExists(text);
              }}
              autoCapitalize="characters"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />
            {codeExists && (
              <Text style={styles.errorText}>
                This company code already exists.
              </Text>
            )}

            <TextInput
              style={styles.input}
              placeholder="Company Name"
              value={companyName}
              onChangeText={setCompanyName}
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <TextInput
              style={styles.input}
              placeholder="Email"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                checkEmailExists(text);
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            {emailExists && (
              <Text style={styles.errorText}>
                This email is already in use.
              </Text>
            )}

            <View style={styles.passwordContainer}>
              <TextInput
                style={styles.passwordInput}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                selectionColor={GlobalStyles.inpColor.colors.selection}
              />
              <TouchableOpacity
                style={styles.eyeIcon}
                onPress={() => setShowPassword(!showPassword)}
              >
                <Entypo
                  name={showPassword ? "eye" : "eye-with-line"}
                  size={24}
                  color={GlobalStyles.btnColor.backgroundColor} // Use global color for icon
                />
              </TouchableOpacity>
            </View>

            <View style={styles.checkboxRow}>
              <Checkbox
                value={isAgreed}
                onValueChange={setIsAgreed}
                color={
                  isAgreed ? GlobalStyles.btnColor.backgroundColor : "#ccc"
                } // Use global color for checked state
                style={styles.checkbox}
              />

              <Text style={styles.label}>
                I agree to the{" "}
                <Text style={styles.link} onPress={() => setTcVisible(true)}>
                  Terms & Privacy Policy
                </Text>
              </Text>
            </View>

            {isLoading ? (
              <ActivityIndicator
                size="large"
                color={GlobalStyles.btnColor.backgroundColor}
                style={styles.RegButton} // Apply button styling to maintain layout
              />
            ) : (
              <TouchableOpacity
                onPress={handleRegister}
                style={[styles.RegButton, GlobalStyles.btnBgShadow]} // Wrapper for gradient button
              >
                <Text style={[styles.RegButtonText, GlobalStyles.btntxtoffset]}>
                  Register
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              onPress={() => navigation.navigate("CompanyLoginScreen")}
              style={styles.hyperlink}
            >
              <Text style={styles.hyperlinkText}>
                Already Registered Your Company? Login here
              </Text>
            </TouchableOpacity>

            <TermsModal
              visible={tcVisible}
              onClose={() => setTcVisible(false)}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 40,
  },
  container: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#bdd9c2", // Slightly transparent background for the form
    borderRadius: 12,
    padding: 25, // Increased padding
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  containerWide: {
    width: "60%",
    alignSelf: "center",
  },
  title: {
    fontSize: GlobalStyles.fontSize.xxxl, // Slightly larger title
    color: GlobalStyles.btnColor.backgroundColor,
    marginBottom: 30, // More space below title
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    textDecorationStyle: "double"
  },
  input: {
    color: "#000",
    width: "100%",
    height: 50,
    borderColor: "#e0e0e0", // Lighter border
    fontSize: GlobalStyles.fontSize.xs - 1, // Consistent with login screen
    borderWidth: 1,
    borderRadius: 10, // More rounded corners
    marginBottom: 18, // More spacing
    paddingHorizontal: 15, // More padding
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
    shadowColor: "#000", // Subtle shadow for input fields
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  errorText: {
    color: "#e74c3c", // A more vibrant red for errors
    fontSize: GlobalStyles.fontSize.xs,
    alignSelf: "flex-start",
    marginBottom: 10,
    marginLeft: 5, // Adjusted margin
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  passwordContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    marginBottom: 18, // Consistent spacing
    borderColor: "#e0e0e0", // Lighter border
    borderWidth: 1,
    borderRadius: 10,
    backgroundColor: "#fff",
    shadowColor: "#000", // Subtle shadow for input fields
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  passwordInput: {
    flex: 1,
    height: 50,
    fontSize: GlobalStyles.fontSize.xs, // Consistent with other inputs
    borderRadius: 10,
    paddingHorizontal: 15,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  eyeIcon: {
    padding: 10,
    right: 5, // Adjust positioning
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20, // More space
    alignSelf: "flex-start", // Align to start for better visual flow
    marginLeft: 5, // Adjust margin
  },
  checkbox: {
    width: 22, // Slightly larger checkbox
    height: 22,
    marginRight: 12, // More space
    borderRadius: 5, // Slightly rounded checkbox
  },
  label: {
    fontFamily: GlobalStyles.inpFont.fontFamily,
    fontSize: GlobalStyles.fontSize.xs - 1, // Consistent font size
    color: "#333", // Darker color for better readability
  },
  link: {
    fontSize: GlobalStyles.fontSize.xs - 1,
    color: GlobalStyles.btnColor.backgroundColor, // Use primary button color for links
    fontFamily: GlobalStyles.inpFont.fontFamily,
    textDecorationLine: "underline",
    fontWeight: "bold", // Make link text bold
  },
  RegButton: {
    width: "100%",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    marginBottom: 16,
  },
  RegButtonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.btn, // Use global button font size
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  hyperlink: {
    marginTop: 10, // Adjusted margin
  },
  hyperlinkText: {
    fontSize: GlobalStyles.fontSize.xs, // Consistent font size
    color: GlobalStyles.btnColor.backgroundColor, // Use primary button color for links
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    textDecorationLine: "underline", // Underline to indicate it's a link
  },
});

export default CompanyRegistrationScreen;
